import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { supabase } from "@/integrations/supabase/client";

const HIDDEN_ROUTES = ["/welcome", "/onboarding/setup", "/app-auth", "/complete-registration"];

export function CookieBanner() {
  const { t } = useTranslation();
  const { consent, acceptAll, rejectAll, openPreferences } = useCookieConsent();
  const { pathname } = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setIsAuthenticated(!!data.session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (consent !== null) return null;
  if (isAuthenticated) return null;
  if (HIDDEN_ROUTES.includes(pathname)) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="mx-auto max-w-3xl rounded-xl border bg-background/95 backdrop-blur-md shadow-2xl p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4">
          <Cookie className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div>
            <h3 className="font-semibold text-sm mb-1">{t("cookies.bannerTitle")}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("cookies.bannerDescription")}{" "}
              <button
                type="button"
                onClick={openPreferences}
                className="underline text-foreground font-medium hover:text-foreground/80"
              >
                {t("cookies.policyLink")}
              </button>
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button variant="outline" size="sm" onClick={rejectAll}>
            {t("cookies.rejectAll")}
          </Button>
          <Button variant="outline" size="sm" onClick={openPreferences}>
            {t("cookies.configure")}
          </Button>
          <Button size="sm" onClick={acceptAll}>
            {t("cookies.acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  );
}
